import { useState, useRef, useEffect } from 'react';
import { MapPin, CheckCheck, X, Search } from 'lucide-react';
import { useERSContext, useMapContext } from '@/context';
import { Checkbox } from '@/components/ui/checkbox/checkbox';
import { StatusTabs } from '../StatusTabs/StatusTabs';
import { flyToAndShowPin, showMarker, removeMarker } from '@/pages/mapview/MapCommon';
import { constants } from '@/lib/constants/constants';

export default function CamerasList() {
  const { cameras, showCameraStreamPopup, postMessageToPopMapWindow, checkedCamerasRef } = useERSContext();
  const { mapRef, mapMarkersRef } = useMapContext();

  const [checkedCameraIds, setCheckedCameraIds] = useState([]);
  const [globalFilter, setGlobalFilter] = useState('');
  const [activeTab, setActiveTab] = useState(constants.TAB_KEYS.ALL);

  const tabs = [{ key: constants.TAB_KEYS.ALL, label: 'All' }];

  const filteredCameras = cameras.filter(camera => {
    const matchesSearch = !globalFilter || camera.displayName?.toLowerCase().includes(globalFilter.toLowerCase());
    return matchesSearch;
  });

  // Keep the ERS context ref in sync so the popup map window
  // can re-send checked camera markers when it opens
  useEffect(() => {
    checkedCamerasRef.current = cameras.filter(c => checkedCameraIds.includes(c.id));
  }, [checkedCameraIds, cameras]);

  function visibleCamera(camera) {
    showMarker({
      category: 'camera',
      item: camera,
      map: mapRef.current,
      markersObj: mapMarkersRef.current,
      showCameraStreamPopup: showCameraStreamPopup,
      funcPostToPopMap: postMessageToPopMapWindow,
    });
  }

  function hideCamera(id) {
    removeMarker?.({
      category: 'camera',
      id,
      markersObj: mapMarkersRef.current,
      funcPostToPopMap: postMessageToPopMapWindow,
    });
  }

  const handleCheckCamera = (camera, isChecked) => {
    setCheckedCameraIds(prev => (isChecked ? [...prev, camera.id] : prev.filter(id => id !== camera.id)));
    if (isChecked) {
      visibleCamera(camera);
    } else {
      hideCamera(camera.id);
    }
  };

  const handleToggleAllCameras = () => {
    const allCameraIds = filteredCameras.map(c => c.id);
    const allChecked = allCameraIds.every(id => checkedCameraIds.includes(id));

    if (allChecked) {
      // Deselect all
      filteredCameras.forEach(camera => {
        hideCamera(camera.id);
      });
      setCheckedCameraIds([]);
    } else {
      // Select all
      filteredCameras.forEach(camera => {
        if (!checkedCameraIds.includes(camera.id)) {
          visibleCamera(camera);
        }
      });
      setCheckedCameraIds(allCameraIds);
    }
  };

  return (
    <div className="flex h-full flex-col bg overflow-hidden">
      <div className="p-3">
        <StatusTabs
          tabs={tabs}
          activeTab={activeTab}
          onTabChange={setActiveTab}
          dataCount={{ [constants.TAB_KEYS.ALL]: cameras.length }}
        />
      </div>

      <div className="px-3 pb-3">
        <div className="border-2 border-border rounded-md flex items-center focus-within:border-primary transition-colors">
          <button
            onClick={handleToggleAllCameras}
            className="shrink-0 h-full px-3 flex items-center justify-center hover:bg-subtle transition-colors border-r border-border"
            title={
              filteredCameras.every(c => checkedCameraIds.includes(c.id)) && filteredCameras.length > 0
                ? 'Deselect All'
                : 'Select All'
            }
          >
            {filteredCameras.every(c => checkedCameraIds.includes(c.id)) && filteredCameras.length > 0 ? (
              <X size={16} />
            ) : (
              <CheckCheck size={16} />
            )}
          </button>
          <div className="flex flex-1 items-center gap-2 px-3.5 py-2">
            <Search size={15} className="text-icon-muted" />
            <input
              type="text"
              placeholder="Search..."
              className="outline-none text-sm w-full bg-transparent text-text"
              value={globalFilter}
              onChange={e => setGlobalFilter(e.target.value)}
            />
          </div>
        </div>
      </div>
      <div className="flex-1 overflow-y-auto px-3">
        {filteredCameras.length === 0 ? (
          <div className="text-center text-muted py-8">No cameras found</div>
        ) : (
          <ul className="space-y-2">
            {filteredCameras.map(camera => (
              <li
                key={camera.id}
                className="flex items-center justify-between p-3 rounded-lg border border-border bg-subtle"
              >
                <div className="flex items-center gap-3">
                  <Checkbox
                    checked={checkedCameraIds.includes(camera.id)}
                    onCheckedChange={isChecked => handleCheckCamera(camera, isChecked)}
                  />
                  <span className="font-medium text-sm">{camera.displayName}</span>
                </div>
                {camera.location?.[0] && (
                  <button
                    onClick={() => {
                      const currentZoom = mapRef.current.getZoom();
                      const targetZoom = currentZoom < 14 ? 14 : currentZoom;
                      flyToAndShowPin(
                        camera.location[0],
                        camera.location[1],
                        targetZoom,
                        mapRef.current,
                        postMessageToPopMapWindow
                      );
                    }}
                    className="p-1 hover:bg-strong rounded transition-colors"
                    aria-label="Fly to camera location"
                  >
                    <MapPin size={16} />
                  </button>
                )}
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}
